import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { PieChart as PieIcon, BarChart3, Activity, ArrowRight } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";

/**
 * Compact "at-a-glance" trio for the Dashboard header — mirrors the three
 * top-line breakdowns on Reports (Stock value by dept · Spend by dept ·
 * Consumption by dept) at reduced height. Manager-only.
 *
 * Always all-departments — the Dashboard doesn't have a dept filter.
 * Fixed 30-day window for consumption/spend, current state for stock value.
 */
const fmt = (n) => Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 });

const Card = ({ title, icon: Icon, testid, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="nb-card p-4 text-left w-full transition hover:-translate-y-0.5 hover:shadow-lg cursor-pointer group"
    title="Open Reports with the same 30-day window"
  >
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon size={16} strokeWidth={2.5} className="text-[#5B3A99]" />}
      <h3 className="font-display text-base flex-1">{title}</h3>
      <ArrowRight
        size={14}
        strokeWidth={2.5}
        className="text-[#5B3A99] opacity-30 group-hover:opacity-100 transition"
      />
    </div>
    <div className="h-40">{children}</div>
  </button>
);

const DashboardChartsRow = () => {
  const [stockValue, setStockValue] = useState(null);
  const [spend, setSpend] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const { colorFor } = useDepartments();
  const nav = useNavigate();

  const openReports = () => nav("/reports?days=30&tab=consumables");

  useEffect(() => {
    api
      .get("/reports/stock-value-by-department")
      .then((r) => setStockValue(r.data))
      .catch(() => {});
    api
      .get("/reports/spend", { params: { days: 30 } })
      .then((r) => setSpend(r.data))
      .catch(() => {});
    api
      .get("/reports/consumption-by-department", { params: { days: 30 } })
      .then((r) => setConsumption(r.data))
      .catch(() => {});
  }, []);

  const pieData = useMemo(
    () =>
      (stockValue?.by_department || []).map((d) => ({
        name: d.department,
        value: d.value,
      })),
    [stockValue],
  );
  const byDept = spend?.by_department || [];

  const hasData =
    (pieData.length && pieData.some((d) => d.value > 0)) ||
    byDept.length ||
    consumption?.series?.length;

  if (!hasData) return null;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6"
      data-testid="dashboard-charts-row"
    >
      <Card
        title="Stock value by dept"
        icon={PieIcon}
        testid="dash-stock-pie"
        onClick={openReports}
      >
        {pieData.length && pieData.some((d) => d.value > 0) ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={26}
                outerRadius={56}
                paddingAngle={2}
                labelLine={false}
                onClick={(entry, _idx, e) => {
                  e?.stopPropagation?.();
                  nav(
                    `/reports?days=30&tab=consumables&dept=${encodeURIComponent(entry.name)}`,
                  );
                }}
                // Inside-the-slice labels so we don't get clipped by the small
                // card height. Only render when the slice is big enough to fit.
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                  if (percent < 0.07) return null;
                  const RAD = Math.PI / 180;
                  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                  const x = cx + r * Math.cos(-midAngle * RAD);
                  const y = cy + r * Math.sin(-midAngle * RAD);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={10}
                      fontWeight={700}
                      style={{ pointerEvents: "none" }}
                    >
                      {`${(percent * 100).toFixed(0)}%`}
                    </text>
                  );
                }}
              >
                {pieData.map((d) => (
                  <Cell key={d.name} fill={colorFor(d.name)} cursor="pointer" />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => `$${fmt(v)}`}
                contentStyle={{ borderRadius: 10, fontSize: 11 }}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                iconSize={7}
                wrapperStyle={{ fontSize: 10 }}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-[#607087] py-8 text-center">No data</div>
        )}
      </Card>

      <Card
        title="Spend by dept (30d)"
        icon={BarChart3}
        testid="dash-spend-by-dept"
        onClick={openReports}
      >
        {byDept.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byDept} layout="vertical" margin={{ left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis
                dataKey="department"
                type="category"
                tick={{ fontSize: 11 }}
                width={78}
              />
              <Tooltip
                formatter={(v) => `$${fmt(v)}`}
                contentStyle={{ borderRadius: 10, fontSize: 11 }}
              />
              <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
                {byDept.map((d) => (
                  <Cell key={d.department} fill={colorFor(d.department)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-[#607087] py-8 text-center">No spend</div>
        )}
      </Card>

      <Card
        title="Consumption by dept (30d)"
        icon={Activity}
        testid="dash-consumption"
        onClick={openReports}
      >
        {consumption?.series?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={consumption.series}
              margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="day" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip
                formatter={(v) => `$${fmt(v)}`}
                contentStyle={{ borderRadius: 10, fontSize: 11 }}
              />
              <Legend
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 10 }}
                iconType="circle"
                iconSize={7}
              />
              {(consumption.departments || []).map((d) => (
                <Line
                  key={d}
                  type="monotone"
                  dataKey={d}
                  stroke={colorFor(d)}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-[#607087] py-8 text-center">No OUT movements</div>
        )}
      </Card>
    </div>
  );
};

export default DashboardChartsRow;
